/**
 * Timezone-safe date-of-birth parsing and validation utilities.
 *
 * Background
 * ----------
 * JavaScript's `new Date('YYYY-MM-DD')` (date-only ISO string) creates a UTC
 * instant (midnight UTC). The familiar accessors `.getDate()`, `.getMonth()`,
 * `.getFullYear()` return values in the *device's local timezone*, not UTC.
 * For any timezone west of UTC the local calendar day is one behind the UTC
 * day, so a naive `date.getDate() === parseInt(d)` comparison silently fails
 * and the picker never emits an onChange -- the form appears to reject the
 * date with no visible error.
 *
 * These two functions replace the unsafe pattern in all three DOB picker
 * components (service-user, RMS, WLC). They follow existing codebase
 * precedents:
 *   - `HarbourField.js` `dateFieldToIso()` uses `getUTCDate()` for
 *     symmetric UTC-parse + UTC-read validation
 *   - `HarbourAddToReferralFlyout.js` uses `dob.split('-')` to decompose
 *     a DOB string without touching the Date constructor
 */

/**
 * Decompose an ISO YYYY-MM-DD date string into its parts without using the
 * Date constructor, avoiding all timezone-related pitfalls.
 *
 * Returns zero-padded strings that match the dropdown option values directly
 * (e.g. day '09', month '03', year '1963'). Returns null when the input is
 * empty, non-string, or not in the expected format.
 *
 * Accepts both padded ('03') and unpadded ('3') month/day segments
 * defensively, even though our pickers always produce zero-padded values.
 *
 * @param {string} value - An ISO date string, e.g. '1963-03-09'
 * @returns {{ year: string, month: string, day: string } | null}
 */
export function parseDobString(value) {
  if (!value || typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;
  return {
    year: match[1],
    month: match[2].padStart(2, '0'),
    day: match[3].padStart(2, '0'),
  };
}

/**
 * Validate that day/month/year parts form a real calendar date and return the
 * ISO string, or null if invalid.
 *
 * Uses the ISO string constructor + getUTC* accessors (symmetric: UTC parse +
 * UTC read) to detect overflow dates like Feb 30 or Apr 31. When the Date
 * constructor receives an impossible date (e.g. '2023-02-30') it wraps to the
 * next valid date (Mar 2), so the getUTC* values no longer match the inputs
 * and we correctly reject.
 *
 * The output is always zero-padded YYYY-MM-DD regardless of input formatting.
 *
 * @param {string} day   - Day value, e.g. '09' or '9'
 * @param {string} month - Month value, e.g. '03' or '3'
 * @param {string} year  - Four-digit year, e.g. '1963'
 * @returns {string | null} ISO date string or null if invalid
 */
export function buildDobString(day, month, year) {
  if (!day || !month || !year) return null;

  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return null;

  // Build the zero-padded ISO string, then parse it as UTC and validate
  // with UTC accessors. If the Date constructor wrapped the date (e.g.
  // Feb 30 -> Mar 2), the UTC accessors will disagree with the inputs.
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  // UTC accessors match the UTC-parsed ISO string -- no timezone drift.
  if (date.getUTCFullYear() !== y || date.getUTCMonth() + 1 !== m || date.getUTCDate() !== d) {
    return null;
  }

  return iso;
}
